import {
    Box,
    Divider,
    Grid,
    Typography,
    Snackbar,
    SnackbarContent,
    IconButton,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    AlertColor,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import CodeEditor from "../CodeEditor";
import {
    EducationSubItem,
    TrialityApiResponse,
} from "../Interfaces/interfaces";
import { EducationLessonPageProps } from "./types";
import React, { useState, useEffect } from "react";
import {
    getLessonCountFromModule,
    getLessonUrl,
    hasUserModifiedDefaultCode,
    isModuleComplete,
    isStarklings,
} from "@js/util/educationUtil";
import TextEditor from "../TextEditor";
import { TEXT_EDITOR_DEFAULT_EMPTY_TEXT } from "@js/components/constants/common";
import { Button } from "./styled";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import { userGlobalContext } from "@js/pages/_app";
import Link from "next/link";
import {
    MODULE_COMPLETE_ID,
    SOLVE_YOURSELF_ID,
} from "@js/educationContent/CairoMummiesContent/eduConstants";
import {
    CairoRunResult,
    STARKLINGS,
} from "@js/components/EducationCourse/eduConfig";

const EducationLesson = (props: EducationLessonPageProps) => {
    const { globalUser, loggedIn, setLoadingSpinner } = userGlobalContext();
    const theme = useTheme();
    const courseName: string = props.courseName;
    const moduleInfo: EducationSubItem = props.moduleInfo;
    const moduleId: number = moduleInfo.id;
    const numLessons: number = getLessonCountFromModule(courseName, moduleId);
    const lesson: EducationSubItem = props.lesson;
    const configSolution: string = lesson.solution || "";
    const lessonId: number = lesson.id;
    const title: string = lesson.title;
    const description: string | undefined = lesson.description;
    const fDescription: any = lesson.formattedDescription
        ? JSON.parse(lesson.formattedDescription)
        : TEXT_EDITOR_DEFAULT_EMPTY_TEXT;
    const defaultStartCode: string = lesson.defaultStartCode
        ? lesson.defaultStartCode
        : "";
    const defaultMiddleCode: string = lesson.defaultMiddleCode
        ? lesson.defaultMiddleCode
        : "";
    const defaultEndCode: string = lesson.defaultEndCode
        ? lesson.defaultEndCode
        : "";
    let initialCode: string = defaultStartCode;
    if (defaultMiddleCode) {
        initialCode += `\n${defaultMiddleCode}`;
    }
    if (defaultEndCode) {
        initialCode += `\n\n${defaultEndCode}`;
    }

    // load saved code progress if present
    const [codeContent, setCodeContent] = useState<string>(
        props.savedCode || initialCode
    );
    //set when code is running so we dont double click and ask server to compile twice
    const [isRunningCode, setIsRunningCode] = useState<boolean>(false);
    //Get the cairo result
    const [cairoRunResult, setCairoRunResult] = useState<CairoRunResult>(
        {} as CairoRunResult
    );
    //Error if present
    const [error, setError] = useState<string>("");
    // handles open/close of snackbar component
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
    }>({ open: false, message: "" });

    const [hasNextAccess, setNextAccess] = useState<boolean | undefined>(
        props.hasNextAccess
    );

    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [lessonProgressMsg, setLessonProgressMsg] = useState<string>("");
    const [lessonProgressMsgType, setLessonProgressMsgType] = useState<
        AlertColor | undefined
    >(undefined);

    useEffect(() => {
        // show a notification if we are loading the user's previous progress
        if (!loggedIn) {
            setLessonProgressMsg(
                "Please log in or none of your progress will be saved"
            );
            setLessonProgressMsgType("error");
            return;
        } else {
            setLessonProgressMsg("");
            setLessonProgressMsgType(undefined);
        }

        if (props.savedCode) {
            if (props.isComplete) {
                setLessonProgressMsg("You have completed this lesson!");
                setLessonProgressMsgType("success");
            } else {
                setLessonProgressMsg(
                    "Loaded your saved progress on this lesson"
                );
                setLessonProgressMsgType("info");
            }
        }
    }, [loggedIn]);

    /**
     * Reset the code in the editor to its default state
     */
    const resetDefault = () => {
        setCodeContent(initialCode);
        const err = hasUserModifiedDefaultCode(
            initialCode,
            courseName,
            moduleId,
            lessonId
        );
        setError(err);
        setLessonProgressMsg("");
        setLessonProgressMsgType(undefined);
    };

    //This determines the logic for whether the next button changes to true, if it is already true then this
    //doesn't change its status
    const setNextAccessLogic = async () => {
        //If not the last lesson of starklings, always set true - else follow the following logic
        if (!(lessonId === numLessons - 1 && isStarklings(courseName))) {
            setNextAccess(true);
            return;
        }

        const resp = await fetch(
            `/api/getAllCourseProgress?address=${globalUser.address}`
        );

        const tar: TrialityApiResponse = await resp.json();
        if (!tar.success) {
            console.error(
                `Failed to fetch all course progress from ${globalUser.address}`,
                tar.err
            );
            return;
        }

        const allCourseProgress = tar.data || [];
        //Only set access to true on this last lesson in starklings if now 100% of lessons are complete
        if (
            isModuleComplete(
                allCourseProgress,
                courseName,
                moduleId,
                numLessons
            )
        ) {
            setNextAccess(true);
        }
    };

    //send code to the backend to be checked
    const checkResults = async () => {
        try {
            setCairoRunResult({} as CairoRunResult);
            setLoadingSpinner(true);
            setIsRunningCode(true);
            const response = await fetch("/api/verifyLesson", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    code: codeContent,
                    courseName,
                    moduleId,
                    lessonId,
                    numLessons,
                }),
            });

            const tar: TrialityApiResponse = await response.json();
            if (tar.success) {
                setSnackbar({
                    open: true,
                    message: tar.data.success
                        ? "Lesson passed!"
                        : "Incorrect, see below for more details",
                });
                setCairoRunResult(tar.data);
                const lessonSuccess = tar.data.success;
                if (lessonSuccess && loggedIn) {
                    setNextAccessLogic();
                    setLessonProgressMsg("You have completed this lesson!");
                    setLessonProgressMsgType("success");
                }
            } else {
                setSnackbar({
                    open: true,
                    message: "Failure, see below for more details",
                });
                setCairoRunResult({
                    success: false,
                    output: {
                        msg: tar.err || "Unknown error verifying lesson...",
                        out: "",
                        lineNumErr: -1,
                        colNumErr: -1,
                    },
                });
            }
        } catch (e: any) {
            setCairoRunResult({
                success: false,
                output: {
                    msg: e.toString(),
                    out: "",
                    lineNumErr: -1,
                    colNumErr: -1,
                },
            });
            setSnackbar({
                open: true,
                message: "Incorrect, see below for more details.",
            });
        } finally {
            setIsRunningCode(false);
            setLoadingSpinner(false);
        }
    };

    /**
     * Format the JSX for the success or error on an lesson
     * @returns - Jsx
     */
    function getLessonsResultJsx() {
        if (!cairoRunResult.output) return null;
        if (cairoRunResult.success) {
            return (
                <>
                    <Typography color="success.main" mb={2}>
                        {cairoRunResult.output.msg}
                    </Typography>
                    <Typography color="success.main" mb={2}>
                        <div
                            style={{
                                whiteSpace: "pre-wrap",
                                textAlign: "left",
                                fontFamily: "monospace",
                            }}
                        >
                            {cairoRunResult.output.out}
                        </div>
                    </Typography>
                </>
            );
        }

        let lineNumMsg = "";
        const hasLineNum =
            cairoRunResult.output.lineNumErr &&
            cairoRunResult.output.lineNumErr > -1 &&
            cairoRunResult.output.colNumErr > -1;

        if (hasLineNum) {
            lineNumMsg = `Error at line ${cairoRunResult.output.lineNumErr} in your editor above: `;
        }

        return (
            <>
                <Typography color="error.main">
                    <div style={{ fontWeight: "bold" }}>{lineNumMsg}</div>
                    <div
                        style={{
                            whiteSpace: "pre-wrap",
                            textAlign: "left",
                            fontFamily: "monospace",
                        }}
                    >
                        {cairoRunResult.output.msg}
                    </div>
                </Typography>
            </>
        );
    }

    /**
     * Get to the previous lesson url
     */
    const getPreviousLessonUrl = () => {
        return getLessonUrl(courseName, moduleId, lessonId - 1);
    };

    /**
     * Get to the next lesson url
     */
    const getNextLessonUrl = () => {
        let nextLessonId = lessonId + 1;
        if (lessonId == numLessons - 1 || lessonId == SOLVE_YOURSELF_ID) {
            nextLessonId = MODULE_COMPLETE_ID;
        }
        return getLessonUrl(courseName, moduleId, nextLessonId);
    };

    //Formatted description for slate
    const [textEditorBody, setTextEditorBody] = useState(fDescription);

    /**
     * Get the proper lesson number to show between back and next
     * @returns the lesson display number
     */
    function getCurrLessonDisplay() {
        if (lessonId == 999) {
            return numLessons;
        }
        return lessonId + 1;
    }

    /** Check if this is the final lesson in the module */
    function isFinalLessonInModule() {
        return getCurrLessonDisplay() === numLessons;
    }

    /**
     * Only show solution button if this is starklings and there is a valid solution
     * @returns - see solution button jsx
     */
    function getSeeSolutionButton() {
        if (courseName === STARKLINGS && configSolution) {
            return (
                <Button
                    variant="contained"
                    onClick={() => {
                        setOpenDialog(true);
                    }}
                    sx={{ marginLeft: "10px", ...props.buttonStyle }}
                >
                    See solution
                </Button>
            );
        }
        return "";
    }

    /**
     * Create the Dialog Jsx for the solution
     * @returns jsx
     */
    function dialogJSX() {
        return (
            <Box>
                <Dialog
                    open={openDialog}
                    onClose={() => {
                        setOpenDialog(false);
                    }}
                    maxWidth={"lg"}
                >
                    <DialogTitle
                        sx={{
                            fontSize: "32px",
                            fontWeight: "bold",
                            lineHeight: "133.4%",
                            marginTop: "47px",
                            textAlign: "center",
                        }}
                    >
                        Solution
                    </DialogTitle>
                    <DialogContent>
                        <CodeEditor
                            content={configSolution}
                            onChange={(value: string) => {
                                //do nothing
                            }}
                            isReadOnly={true}
                        ></CodeEditor>
                    </DialogContent>
                    <Box
                        display="flex"
                        justifyContent="space-evenly"
                        alignItems="baseline"
                    ></Box>
                </Dialog>
            </Box>
        );
    }

    return (
        <>
            {dialogJSX()}
            <Grid
                container
                direction="row"
                rowSpacing={1}
                sx={{ paddingTop: "10px" }}
            >
                <Grid
                    item
                    md={4}
                    sx={{
                        paddingLeft: "20px",
                        paddingRight: "20px",
                        paddingBottom: "20px",
                        whiteSpace: "nowrap",
                        WebkitScrollbar: { width: "0.4em" },
                        WebkitScrollbarTrack: {
                            boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                            webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                        },
                        WebkitScrollbarThumb: {
                            backgroundColor: "rgba(0,0,0,.1)",
                            outline: "1px solid slategrey",
                        },
                        overflow: "scroll",
                        overflowY: "scroll",
                    }}
                    maxHeight="calc(100vh - 135px)"
                >
                    <Typography variant="h5">{title}</Typography>
                    {fDescription ? (
                        <TextEditor
                            readOnly={true}
                            setBody={setTextEditorBody}
                            body={textEditorBody}
                        />
                    ) : (
                        <Typography
                            style={{
                                whiteSpace: "pre-line",
                                marginBottom: "50px",
                            }}
                        >
                            {description}
                        </Typography>
                    )}
                    {/* Fixed Scroll bar */}
                    {/* <Box
                        style={{
                            position: "absolute",
                            height: "183px",
                            top: "0px",
                            // "& + div": {
                            //     zIndex: "5",
                            //     width: "12px",
                            //     right: "0",
                            // },
                            float: "right",
                        }}
                    >
                        s
                        <Box
                            id="styler"
                            style={{
                                transition:
                                    "background-color .1s ease-out,margin .1s ease-out,height .1s ease-out",
                                margin: "5px 5px 5px 0",
                                borderRadius: " 20px",
                                height: "calc(100% - 10px)",
                                display: "block",
                                backgroundColor: "red", //"hsla(0,0%,70.6%,.38)",
                            }}
                        />
                    </Box> */}
                </Grid>
                <Divider
                    sx={{ ml: 0.5, mr: 0.5 }}
                    flexItem
                    orientation="vertical"
                />
                <Grid
                    item
                    md={7}
                    sx={{
                        paddingLeft: "20px",
                        paddingRight: "20px",
                    }}
                    display="flex"
                    direction="column"
                >
                    <CodeEditor
                        content={codeContent}
                        onChange={(value: string) => {
                            setCodeContent(value);
                            const err = hasUserModifiedDefaultCode(
                                value,
                                courseName,
                                moduleId,
                                lessonId
                            );
                            setError(err);
                        }}
                        isReadOnly={false}
                        severity={lessonProgressMsgType}
                        message={lessonProgressMsg}
                    />
                    <div style={{ marginTop: "1rem", flexGrow: 1 }}>
                        <Button
                            variant="contained"
                            onClick={checkResults}
                            disabled={!!error || isRunningCode}
                            sx={{ marginRight: "10px", ...props.buttonStyle }}
                        >
                            Verify answer
                        </Button>
                        <Button
                            variant="contained"
                            onClick={resetDefault}
                            sx={{ ...props.buttonStyle }}
                        >
                            Reset to Default Script
                        </Button>
                        {getSeeSolutionButton()}
                        {error ? (
                            <Typography align="right" mt={2}>
                                {error}
                            </Typography>
                        ) : (
                            <></>
                        )}
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                        }}
                    >
                        <div>
                            <Link href={getPreviousLessonUrl()} passHref>
                                <Button
                                    disableElevation
                                    disableFocusRipple
                                    disableRipple
                                    disableTouchRipple
                                    startIcon={<WestIcon />}
                                    disabled={lessonId == 0 || lessonId == 999}
                                >
                                    Back
                                </Button>
                            </Link>
                        </div>
                        <div>
                            <Typography>
                                {getCurrLessonDisplay()} / {numLessons}
                            </Typography>
                        </div>
                        <div style={{ textAlign: "right" }}>
                            <Link href={getNextLessonUrl()} passHref>
                                <Button
                                    disableElevation
                                    disableFocusRipple
                                    disableRipple
                                    disableTouchRipple
                                    disabled={!hasNextAccess}
                                    endIcon={<EastIcon />}
                                >
                                    Next
                                </Button>
                            </Link>
                        </div>
                    </div>
                </Grid>
            </Grid>
            <Snackbar
                autoHideDuration={5000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                open={snackbar.open}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                key={"lesson-result"}
            >
                <SnackbarContent
                    message={snackbar.message}
                    sx={{
                        color: theme.palette.info.dark,
                        backgroundColor: cairoRunResult.success
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                    }}
                    action={
                        <IconButton
                            onClick={() =>
                                setSnackbar({ ...snackbar, open: false })
                            }
                        >
                            <CloseIcon />
                        </IconButton>
                    }
                ></SnackbarContent>
            </Snackbar>
            {getLessonsResultJsx()}
        </>
    );
};

export default EducationLesson;
